@if (report; as data) {
  <div class="srr">
    <header class="srr-header">
      <div class="srr-header__text">
        <h2 class="srr-title">Shift Report</h2>
        <p class="srr-subtitle">
          {{ data.meta.location_label }} · {{ data.meta.employee_count }}
          {{ data.meta.employee_count === 1 ? 'employee' : 'employees' }}
        </p>
      </div>
      <div class="srr-exports">
        <button
          type="button"
          class="srr-export-btn"
          [disabled]="exportingPdf || exportingExcel || !data.employees.length"
          (click)="exportPdf()"
        >
          {{ exportingPdf ? 'Exporting…' : 'Export PDF' }}
        </button>
        <button
          type="button"
          class="srr-export-btn"
          [disabled]="exportingPdf || exportingExcel || !data.employees.length"
          (click)="exportExcel()"
        >
          {{ exportingExcel ? 'Exporting…' : 'Export Excel' }}
        </button>
      </div>
    </header>

    @if (!data.employees.length) {
      <div class="srr-empty">
        <h3>No shift data for these filters</h3>
        <p>Try another date, location, or employee selection.</p>
      </div>
    } @else {
      <div
        class="srr-tabs-wrap"
        [class.srr-tabs-wrap--fade-left]="canScrollLeft"
        [class.srr-tabs-wrap--fade-right]="canScrollRight"
      >
        <button
          type="button"
          class="srr-tabs-nav srr-tabs-nav--left"
          [class.srr-tabs-nav--visible]="canScrollLeft"
          [disabled]="!canScrollLeft"
          aria-label="Scroll employee tabs left"
          (click)="scrollTabs('left')"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 3.5L5.5 8L10 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          #tabsScroll
          class="srr-tabs"
          role="tablist"
          aria-label="Employees"
          (scroll)="onTabsScroll()"
        >
          @for (emp of data.employees; track trackByEmployeeId($index, emp)) {
            <button
              type="button"
              role="tab"
              class="srr-tab"
              [class.srr-tab--active]="activeEmployeeId === emp.employee_id"
              [attr.aria-selected]="activeEmployeeId === emp.employee_id"
              (click)="selectEmployee(emp.employee_id)"
            >
              {{ emp.name }}
            </button>
          }
        </div>
        <button
          type="button"
          class="srr-tabs-nav srr-tabs-nav--right"
          [class.srr-tabs-nav--visible]="canScrollRight"
          [disabled]="!canScrollRight"
          aria-label="Scroll employee tabs right"
          (click)="scrollTabs('right')"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6 3.5L10.5 8L6 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      @if (activeEmployee; as emp) {
        <div class="srr-employee">
          <div class="srr-employee__identity">
            <h3 class="srr-employee__name">{{ emp.name }}</h3>
            <p class="srr-employee__meta">
              {{ emp.designation || 'Employee' }}
              @if (emp.location_name) {
                <span> · {{ emp.location_name }}</span>
              }
            </p>
          </div>
          <div class="srr-employee__shift">
            <div class="srr-shift-date">
              {{ data.meta.date | date: 'EEEE, MMMM d, y' }}
            </div>
            <div class="srr-shift-divider" aria-hidden="true"></div>
            <div class="srr-shift-item">
              <span class="srr-shift-item__label">
                Check in
                <app-info-tip label="Check in" [text]="tips.checkIn"></app-info-tip>
              </span>
              <span class="srr-shift-item__value">{{ emp.shift.check_in || '—' }}</span>
            </div>
            <div class="srr-shift-divider" aria-hidden="true"></div>
            <div class="srr-shift-item">
              <span class="srr-shift-item__label">
                Check out
                <app-info-tip label="Check out" [text]="tips.checkOut"></app-info-tip>
              </span>
              <span class="srr-shift-item__value">{{ emp.shift.check_out || '—' }}</span>
            </div>
          </div>
        </div>

        <section class="srr-revenue">
          <h4 class="srr-section-title">Revenue summary</h4>
          <div class="srr-kpi-row">
            <div class="srr-kpi">
              <span class="srr-kpi__label">
                Total revenue
                <app-info-tip label="Total revenue" [text]="tips.revenueTotal"></app-info-tip>
              </span>
              <span class="srr-kpi__value">${{ formatMoney(emp.revenue.total) }}</span>
            </div>
            <div class="srr-kpi">
              <span class="srr-kpi__label">
                Unlimited
                <app-info-tip label="Unlimited" [text]="tips.revenueUnlimited"></app-info-tip>
              </span>
              <span class="srr-kpi__value">${{ formatMoney(emp.revenue.unlimited) }}</span>
            </div>
            <div class="srr-kpi">
              <span class="srr-kpi__label">
                Single Wash
                <app-info-tip label="Single Wash" [text]="tips.revenueSingleWash"></app-info-tip>
              </span>
              <span class="srr-kpi__value">${{ formatMoney(emp.revenue.single_wash) }}</span>
            </div>
            <div class="srr-kpi">
              <span class="srr-kpi__label">
                Prepaid
                <app-info-tip label="Prepaid" [text]="tips.revenuePrepaid"></app-info-tip>
              </span>
              <span class="srr-kpi__value">${{ formatMoney(emp.revenue.prepaid) }}</span>
            </div>
            <div class="srr-kpi">
              <span class="srr-kpi__label">
                Add-ons
                <app-info-tip label="Add-ons" [text]="tips.revenueAddOns"></app-info-tip>
              </span>
              <span class="srr-kpi__value">${{ formatMoney(emp.revenue.add_ons) }}</span>
            </div>
          </div>
        </section>

        <div class="srr-tx-row">
          <section class="srr-panel">
            <h4 class="srr-panel__title">Cash transactions</h4>
            <dl class="srr-dl">
              <div class="srr-dl__row">
                <dt>
                  Opening balance
                  <app-info-tip label="Opening balance" [text]="tips.openingBalance"></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.cash.opening_balance) }}</dd>
              </div>
              <div class="srr-dl__row">
                <dt>
                  Extra cash added
                  <app-info-tip
                    label="Extra cash added"
                    [text]="tips.extraCashAdded"
                  ></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.cash.extra_cash_added) }}</dd>
              </div>
              <div class="srr-dl__row">
                <dt>
                  Cash collected
                  <span class="srr-dl__hint">({{ emp.cash.cash_txn_count }} transactions)</span>
                  <app-info-tip label="Cash collected" [text]="tips.cashCollected"></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.cash.cash_collected) }}</dd>
              </div>
              <div class="srr-dl__row srr-dl__row--emphasis">
                <dt>
                  Expected closing
                  <app-info-tip
                    label="Expected closing"
                    [text]="tips.expectedClosing"
                  ></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.cash.expected_closing) }}</dd>
              </div>
              <div class="srr-dl__row">
                <dt>
                  Actual closing
                  <app-info-tip label="Actual closing" [text]="tips.actualClosing"></app-info-tip>
                </dt>
                <dd>
                  {{
                    emp.cash.actual_closing === null
                      ? '—'
                      : '$' + formatMoney(emp.cash.actual_closing)
                  }}
                </dd>
              </div>
              <div class="srr-dl__row">
                <dt>
                  Delta
                  <app-info-tip label="Delta" [text]="tips.delta"></app-info-tip>
                </dt>
                <dd>
                  {{ emp.cash.delta === null ? '—' : '$' + formatMoney(emp.cash.delta) }}
                </dd>
              </div>
            </dl>
          </section>

          <section class="srr-panel">
            <h4 class="srr-panel__title">Card transactions</h4>
            <dl class="srr-dl">
              <div class="srr-dl__row">
                <dt>
                  Transactions ({{ emp.card.transactions_count }} transactions)
                  <app-info-tip
                    label="Card transactions"
                    [text]="tips.cardTransactions"
                  ></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.card.transactions_amount) }}</dd>
              </div>
              <div class="srr-dl__row">
                <dt>
                  Refunds
                  <app-info-tip label="Refunds" [text]="tips.cardRefunds"></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.card.refunds) }}</dd>
              </div>
              <div class="srr-dl__row srr-dl__row--emphasis">
                <dt>
                  Net credit
                  <app-info-tip label="Net credit" [text]="tips.netCredit"></app-info-tip>
                </dt>
                <dd>${{ formatMoney(emp.card.net_credit) }}</dd>
              </div>
            </dl>
          </section>
        </div>

        <section class="srr-panel srr-packages">
          <h4 class="srr-packages__title">
            Wash packages
            <app-info-tip label="Wash packages" [text]="tips.washPackages"></app-info-tip>
          </h4>
          @if (!emp.packages.length) {
            <p class="srr-packages__empty">No packages sold for this shift.</p>
          } @else {
            <ul class="srr-package-list">
              @for (pkg of emp.packages; track pkg.name) {
                <li class="srr-package">
                  <span class="srr-package__name">{{ pkg.name }}</span>
                  <div class="srr-package__track">
                    <div class="srr-package__bar" [style.width]="packageBarWidth(pkg.count)"></div>
                  </div>
                  <span class="srr-package__count">{{ pkg.count }}</span>
                </li>
              }
            </ul>
            <p class="srr-packages__total">
              {{ emp.total_washes }} washes total
              <app-info-tip label="Washes total" [text]="tips.totalWashes"></app-info-tip>
            </p>
          }
        </section>

        <section class="srr-shift-summary">
          <h4 class="srr-section-title">Shift summary</h4>
          <div class="srr-summary-row">
            <section class="srr-summary-card">
              <h5 class="srr-summary-card__title">Prepaid</h5>
              <dl class="srr-dl srr-dl--compact">
                <div class="srr-dl__row">
                  <dt>
                    Packages Sold
                    <app-info-tip
                      label="Prepaid packages sold"
                      [text]="tips.prepaidSold"
                    ></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.prepaid.sold }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Redeemed
                    <app-info-tip
                      label="Prepaid redeemed"
                      [text]="tips.prepaidRedeemed"
                    ></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.prepaid.redeemed ?? 0 }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Revenue
                    <app-info-tip
                      label="Prepaid revenue"
                      [text]="tips.prepaidRevenue"
                    ></app-info-tip>
                  </dt>
                  <dd>${{ formatMoney(emp.summary.prepaid.revenue) }}</dd>
                </div>
              </dl>
            </section>

            <section class="srr-summary-card">
              <h5 class="srr-summary-card__title">Add-ons</h5>
              <dl class="srr-dl srr-dl--compact">
                <div class="srr-dl__row">
                  <dt>
                    Packages Sold
                    <app-info-tip label="Add-ons sold" [text]="tips.addOnsSold"></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.add_ons.sold }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Revenue
                    <app-info-tip
                      label="Add-ons revenue"
                      [text]="tips.addOnsRevenue"
                    ></app-info-tip>
                  </dt>
                  <dd>${{ formatMoney(emp.summary.add_ons.revenue) }}</dd>
                </div>
              </dl>
            </section>

            <section class="srr-summary-card">
              <h5 class="srr-summary-card__title">Single Wash</h5>
              <dl class="srr-dl srr-dl--compact">
                <div class="srr-dl__row">
                  <dt>
                    Packages Sold
                    <app-info-tip
                      label="Single washes sold"
                      [text]="tips.singleWashSold"
                    ></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.single_wash.sold }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Revenue
                    <app-info-tip
                      label="Single wash revenue"
                      [text]="tips.singleWashRevenue"
                    ></app-info-tip>
                  </dt>
                  <dd>${{ formatMoney(emp.summary.single_wash.revenue) }}</dd>
                </div>
              </dl>
            </section>

            <section class="srr-summary-card">
              <h5 class="srr-summary-card__title">Membership</h5>
              <dl class="srr-dl srr-dl--compact">
                <div class="srr-dl__row">
                  <dt>
                    Packages Sold
                    <app-info-tip
                      label="Memberships sold"
                      [text]="tips.membershipSold"
                    ></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.membership.sold }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Redeemed
                    <app-info-tip
                      label="Membership redeemed"
                      [text]="tips.membershipRedeemed"
                    ></app-info-tip>
                  </dt>
                  <dd>{{ emp.summary.membership.redeemed ?? 0 }}</dd>
                </div>
                <div class="srr-dl__row">
                  <dt>
                    Revenue
                    <app-info-tip
                      label="Membership revenue"
                      [text]="tips.membershipRevenue"
                    ></app-info-tip>
                  </dt>
                  <dd>${{ formatMoney(emp.summary.membership.revenue) }}</dd>
                </div>
              </dl>
            </section>
          </div>
        </section>
      }
    }
  </div>
}
